import React from 'react';
import {
  X, Users, Phone, Mail, MapPin, FileText, Calendar, BellRing,
  MessageSquare, Plus, ShoppingCart, Edit, Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const fmtDateTime = (ts) =>
  new Date(ts).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

/**
 * Lead Detail modal — read-only summary of a lead's contact + project info
 * with call logs and quotes. Footer has the three primary CTAs (Send Quote /
 * Order Builder / Edit Lead).
 *
 * Visual-only; all state + handlers live in the parent (ManageLeads).
 */
export default function LeadDetailModal({
  open,
  lead,
  leadQuotes,
  serviceBadge,           // (serviceType) => { label, color }
  statusBadge,            // (status) => className
  statusIcon,             // (status) => JSX
  statusLabel,            // (status) => string
  onClose,
  onOpenCallLog,
  onSendQuote,
  onOpenOrder,
  onEdit,
  onDownloadQuotePDF,
}) {
  if (!open || !lead) return null;
  const svc = serviceBadge(lead.service_type);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-[#006400] to-[#004d00]">
          <div>
            <h2 className="text-xl font-bold text-white">{lead.customer_name}</h2>
            <div className="flex items-center gap-2 mt-2">
              <span
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold text-white"
                style={{ backgroundColor: svc.color }}
              >
                {svc.label}
              </span>
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${statusBadge(lead.status)}`}>
                {statusIcon(lead.status)}
                {statusLabel(lead.status)}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Contact & Project Info */}
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-[#27292E] mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Contact Information
                </h3>
                <div className="space-y-3">
                  {lead.customer_phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <a href={`tel:${lead.customer_phone}`} className="text-[#2F5E96] hover:underline">
                        {lead.customer_phone}
                      </a>
                    </div>
                  )}
                  {lead.customer_email && (
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <a href={`mailto:${lead.customer_email}`} className="text-[#2F5E96] hover:underline">
                        {lead.customer_email}
                      </a>
                    </div>
                  )}
                  {lead.customer_address && (
                    <div className="flex items-start gap-3">
                      <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                      <span className="text-gray-700">{lead.customer_address}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-[#27292E] mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Project Details
                </h3>
                <div className="space-y-3 text-sm">
                  {lead.project_size && (
                    <div>
                      <span className="text-gray-500">Project Size:</span>
                      <span className="ml-2 font-medium">{lead.project_size}</span>
                    </div>
                  )}
                  {lead.project_timeline && (
                    <div>
                      <span className="text-gray-500">Timeline:</span>
                      <span className="ml-2 font-medium">{lead.project_timeline}</span>
                    </div>
                  )}
                  {lead.project_description && (
                    <div>
                      <span className="text-gray-500 block mb-1">Description:</span>
                      <p className="text-gray-700 bg-white p-2 rounded border">{lead.project_description}</p>
                    </div>
                  )}
                  {lead.notes && (
                    <div>
                      <span className="text-gray-500 block mb-1">Notes:</span>
                      <p className="text-gray-700 bg-white p-2 rounded border whitespace-pre-wrap">{lead.notes}</p>
                    </div>
                  )}
                </div>
              </div>

              {lead.appointment_date && (
                <div className="bg-[#2F5E96]/10 rounded-xl p-4 border border-[#2F5E96]/20">
                  <h3 className="font-semibold text-[#2F5E96] mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Scheduled Appointment
                  </h3>
                  <p className="text-[#2F5E96] font-medium">
                    {lead.appointment_date} {lead.appointment_time && `at ${lead.appointment_time}`}
                  </p>
                </div>
              )}

              {lead.reminder_date && (
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                  <h3 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                    <BellRing className="w-4 h-4" />
                    Reminder Set
                  </h3>
                  <p className="text-amber-700 font-medium">{lead.reminder_date}</p>
                  {lead.reminder_note && (
                    <p className="text-amber-600 text-sm mt-1">{lead.reminder_note}</p>
                  )}
                </div>
              )}
            </div>

            {/* Right Column - Call Logs */}
            <div>
              <div className="bg-gray-50 rounded-xl p-4 h-full">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-[#27292E] flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Call Logs ({lead.call_logs?.length || 0})
                  </h3>
                  <Button
                    onClick={onOpenCallLog}
                    size="sm"
                    className="bg-[#006400] hover:bg-[#004d00] text-white"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Note
                  </Button>
                </div>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {lead.call_logs && lead.call_logs.length > 0 ? (
                    lead.call_logs.slice().reverse().map((log, idx) => (
                      <div key={log.id || idx} className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900 text-sm">{log.user_name}</span>
                          <span className="text-xs text-gray-400">{fmtDateTime(log.timestamp)}</span>
                        </div>
                        <p className="text-gray-600 text-sm">{log.note}</p>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-400 bg-white rounded-lg border border-dashed border-gray-200">
                      <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No call logs yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Quotes Section */}
          {leadQuotes.length > 0 && (
            <div className="mt-6 bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-[#27292E] flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4" />
                Quotes ({leadQuotes.length})
              </h3>
              <div className="space-y-2">
                {leadQuotes.map((quote) => (
                  <div key={quote.id} className="bg-white p-3 rounded-lg border border-gray-200 flex items-center justify-between">
                    <div>
                      <span className="font-medium text-[#2F5E96]">{quote.reference}</span>
                      <span className="text-xs text-gray-400 ml-3">
                        {new Date(quote.created_at).toLocaleDateString('en-GB', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        })}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-bold text-gray-700">
                        £{(quote.grand_total || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); onDownloadQuotePDF(quote); }}
                        className="text-sm text-[#2F5E96] hover:text-[#1e3a5f] font-medium flex items-center gap-1 bg-[#2F5E96]/10 px-3 py-1 rounded-lg"
                        data-testid={`modal-download-quote-${quote.id}`}
                      >
                        <Download className="w-3 h-3" />
                        PDF
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-100 bg-gray-50 flex flex-wrap gap-2">
          <Button onClick={onSendQuote} className="flex-1 bg-[#006400] hover:bg-[#004d00] text-white">
            <FileText className="w-4 h-4 mr-2" />
            Send Quote
          </Button>
          <Button onClick={onOpenOrder} variant="outline" className="flex-1 border-[#2F5E96] text-[#2F5E96] hover:bg-[#2F5E96]/5">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Order Builder
          </Button>
          <Button onClick={onEdit} variant="outline" className="flex-1">
            <Edit className="w-4 h-4 mr-2" />
            Edit Lead
          </Button>
        </div>
      </div>
    </div>
  );
}
